<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="720" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form class="fdlg__form" @submit.prevent="submit">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">{{ title }}</h2>
            <p class="fdlg__sub">{{ subtitle }}</p>
          </div>
          <v-btn icon aria-label="Închide" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <div v-if="mode === 'replace'" class="dd__replace">
            <v-icon class="dd__replace-icon" aria-hidden="true">mdi-file-replace-outline</v-icon>
            <div>
              <p class="dd__replace-title">Se creează un departament nou, iar „{{ dept.name }}” se ascunde.</p>
              <ul>
                <li><strong>Nimic nu se mută.</strong> Membrii, echipamentele și demersurile rămân la departamentul vechi.</li>
                <li>
                  Cei din departamentul nou văd demersurile celui vechi. Cât timp cel vechi nu are Șef, Șeful celui nou
                  le aprobă pe cele rămase deschise.
                </li>
                <li>Departamentul vechi rămâne ca document: demersurile lui își păstrează denumirea și istoria.</li>
                <li>
                  După salvare, mutați manual oamenii (Utilizatori) și echipamentele (Echipamente → Transfer) la
                  departamentul nou.
                </li>
              </ul>
              <p class="dd__replace-hint">Dacă s-a schimbat doar denumirea, folosiți „Redenumește”.</p>
            </div>
          </div>

          <div v-if="mode === 'replace' && openRequests > 0" class="dd__open" role="alert">
            <v-icon class="dd__open-icon" aria-hidden="true">mdi-alert-octagon-outline</v-icon>
            <p>{{ openRequestsText }}</p>
          </div>

          <div v-if="mode === 'rename' && dept && dept.used" class="cfg-used-note">
            <v-icon small>mdi-alert-outline</v-icon>
            <p>
              În uz: {{ usageText(dept.usage) }}. Denumirea nouă se vede pe echipamente și la utilizatori; demersurile
              depuse păstrează denumirea veche, iar membrii le văd în continuare.
            </p>
          </div>

          <section v-if="mode !== 'move'" class="form-section">
            <div class="form-section__label"><v-icon small>mdi-form-textbox</v-icon>Denumire</div>
            <v-text-field
              v-model="name"
              :label="mode === 'replace' ? 'Denumirea departamentului nou*' : 'Denumirea*'"
              prepend-inner-icon="mdi-domain"
              outlined
              dense
              maxlength="250"
              :error-messages="nameError"
              @input="nameError = ''"
            ></v-text-field>
            <p v-if="mode === 'rename'" class="form-section__note dd__note">
              Departamentul s-a reorganizat (nu doar altă denumire)? Folosiți „Versiune nouă”.
            </p>
          </section>

          <section v-if="mode !== 'rename'" class="form-section">
            <div class="form-section__label">
              <v-icon small>mdi-file-tree-outline</v-icon>{{ mode === "move" ? "Mută sub" : "Face parte din" }}
            </div>
            <p v-if="mode === 'move'" class="form-section__note">
              Se schimbă doar locul în structură; membrii, echipamentele și demersurile rămân la departament.
            </p>
            <v-autocomplete
              v-model="parentId"
              :items="parentOptions"
              :filter="matchDepartment"
              item-text="name"
              item-value="id"
              label="Departamentul părinte*"
              prepend-inner-icon="mdi-file-tree-outline"
              outlined
              dense
              :menu-props="{ contentClass: 'menu-wrap', maxHeight: 360 }"
              no-data-text="Niciun departament găsit"
              :error-messages="parentError"
              @change="parentError = ''"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="paths[item.id]">{{ paths[item.id] }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <p v-if="parentId && paths[parentId]" class="form-section__note dd__path">în {{ paths[parentId] }}</p>
            <p v-if="parentId && parentId !== rootId" class="form-section__note dd__level">
              <v-icon small>mdi-account-group-outline</v-icon>
              <span>Utilizatorii pot fi membri doar ai departamentelor de pe primul nivel (direct sub Universitate).</span>
            </p>
          </section>

          <section v-if="mode === 'replace'" class="form-section dd__confirm">
            <v-checkbox
              v-model="understood"
              hide-details
              class="mt-0 mb-2"
              label="Am înțeles: nimic nu se mută automat; oamenii și echipamentele le mut eu"
            ></v-checkbox>
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="close">Anulare</v-btn>
          <v-btn class="btn-green cfg-btn" large type="submit" :loading="saving" :disabled="mode === 'replace' && !understood">
            <v-icon left>{{ mode === "replace" ? "mdi-file-replace-outline" : "mdi-content-save-outline" }}</v-icon>
            {{ mode === "replace" ? "Creează versiunea nouă" : "Salvează" }}
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as helper from "@/helper/helper";
import { cleanText, count, plain, usageText } from "./format";

const ROOT_ID = 1;
// the server's rule for "Versiune nouă": another name, case and extra spaces do not count
const sameName = (a, b) => cleanText(a).localeCompare(cleanText(b), "ro", { sensitivity: "accent" }) === 0;

/**
 * Add / rename / move / "Versiune nouă" of a department. Emits submit({ mode, dept, name, parent_id });
 * the tab saves it (a rename in use goes through the 409 warning) and calls close() when done.
 */
export default {
  name: "DepartmentDialog",
  props: {
    departments: { type: Array, default: () => [] },
    // id -> "Universitatea › Facultatea ..." (the ancestors)
    paths: { type: Object, default: () => ({}) },
    // id -> Set of the ids under it (a department cannot move under its own sub-units)
    descendants: { type: Function, required: true },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      mode: "create",
      dept: null,
      name: "",
      parentId: null,
      understood: false,
      nameError: "",
      parentError: "",
      rootId: ROOT_ID,
    };
  },
  computed: {
    title() {
      return {
        create: "Departament nou",
        rename: "Redenumește departamentul",
        move: "Mută departamentul",
        replace: "Versiune nouă a departamentului",
      }[this.mode];
    },
    subtitle() {
      if (this.mode === "create") return "Apare în listele de alegere pentru echipamente, utilizatori și demersuri.";
      return this.dept ? this.dept.name : "";
    },
    // the university first, then by name (as the tab sorts them)
    parentOptions() {
      const blocked = this.mode === "move" && this.dept ? this.descendants(this.dept.id) : new Set();
      const current = this.dept ? this.dept.parent_id : null;
      return this.departments
        .filter((d) => (!d.is_hidden || d.id === current || d.id === this.parentId) && !blocked.has(d.id))
        .filter((d) => !(this.mode === "move" && this.dept && d.id === this.dept.id))
        .sort((a, b) => Number(b.id === ROOT_ID) - Number(a.id === ROOT_ID));
    },
    openRequests() {
      return Number(this.dept && this.dept.usage && this.dept.usage.openRequests) || 0;
    },
    // the requests stay at the old department, so only its Șef can still approve them
    openRequestsText() {
      const n = this.openRequests;
      if (n === 1) {
        return (
          "1 demers al departamentului este încă în lucru. Nimic nu se mută: Șeful lui trebuie să rămână Șef " +
          "al departamentului vechi până îl aprobă, altfel nu-l mai vede nimeni."
        );
      }
      return (
        `${count(n, "demers", "demersuri")} ale departamentului sunt încă în lucru. Nimic nu se mută: Șeful lor ` +
        "trebuie să rămână Șef al departamentului vechi până le aprobă, altfel nu le mai vede nimeni."
      );
    },
  },
  methods: {
    usageText,
    open(mode, dept, parentId) {
      this.mode = mode;
      this.dept = dept || null;
      this.name = mode === "rename" || mode === "replace" ? dept.name : "";
      this.parentId = parentId !== undefined ? parentId : dept ? dept.parent_id : 1;
      this.understood = false;
      this.nameError = "";
      this.parentError = "";
      this.dialog = true;
      helper.scrollToTop(this);
    },
    close() {
      this.dialog = false;
    },
    matchDepartment(item, query) {
      const q = plain(query);
      return plain(item.name).includes(q) || plain(this.paths[item.id]).includes(q);
    },
    submit() {
      const name = cleanText(this.name);
      if (this.mode !== "move" && !name) {
        this.nameError = "Denumirea este obligatorie";
        return;
      }
      if (this.mode === "replace" && sameName(name, this.dept.name)) {
        this.nameError = "Versiunea nouă trebuie să aibă altă denumire. Pentru aceeași denumire folosiți „Redenumește”.";
        return;
      }
      if (this.mode !== "rename" && !this.parentId) {
        this.parentError = "Alegeți departamentul părinte";
        return;
      }
      this.$emit("submit", { mode: this.mode, dept: this.dept, name, parent_id: this.parentId });
    },
  },
};
</script>

<style scoped>
.dd__replace {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--amber-bg);
  border: 1px solid #efd3a0;
  color: #5c3a07;
  line-height: 1.5;
}
.dd__replace-icon.v-icon {
  align-self: flex-start;
  flex: none;
  font-size: 32px;
  color: var(--amber);
}
.dd__replace-title {
  margin: 0 0 6px;
  font-weight: 700;
  font-size: 1.05rem;
  overflow-wrap: anywhere;
}
.dd__replace ul {
  margin: 0;
  padding-left: 18px;
}
.dd__replace li + li {
  margin-top: 4px;
}
.dd__replace-hint {
  margin: 8px 0 0;
  font-size: 0.92rem;
}
.dd__open {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 14px;
  background: var(--brick-bg);
  border: 1px solid #f1c3bf;
  color: #8f2a24;
  font-weight: 600;
  line-height: 1.5;
}
.dd__open p {
  margin: 0;
}
.dd__open-icon.v-icon {
  flex: none;
  font-size: 28px;
  color: var(--brick);
}
.dd__note {
  margin-top: -14px;
}
.dd__level {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.4;
}
.dd__level .v-icon {
  flex: none;
  margin-top: 1px;
  color: var(--ink-3);
}
.dd__path {
  margin-top: -14px;
  overflow-wrap: anywhere;
}
.dd__confirm {
  border-color: #efd3a0;
}
</style>
